import React, {useContext, useEffect, useState} from "react";
import {AlertContext} from "./AlertContex";
import {AlertTypes, apiClient, enumNamesToSelect, Grid, setAlert as setUiAlert, FormHelperText, TextField, Typography, MenuItem} from "@leap4/frontend";
import {dataPointsUrl, GroupsBaseUrl} from "../../../../../config/generals";
import {useDispatch} from "react-redux";
import {AlertType} from "../../enums";
import {CronOrIntervalHour} from "../../enums/cronOrIntervalHour";
import {DeltaParamsForm} from "./TypeParams/DeltaParamsForm";
import {ExpressionParamsForm} from "./TypeParams/ExpressionParamsForm";
import {DiscrepancyParamsForm} from "./TypeParams/DiscrepancyParamsForm";
import {ThresholdParamsForm} from "./TypeParams/ThresholdParamsForm";
import {
    initialDeltaParams,
    initialDiscrepancyParams,
    initialExpressionParams,
    initialThresholdParams
} from "./Alert";

export interface IDetailsFormProps {
    mode: "create" | "edit"
}

const fieldsDataPoints = {
    name: 1,
    type: 1
}

const fieldsGroups = {
    name: 1,
    method: 1
}

const CronOrIntervalSelect = enumNamesToSelect(CronOrIntervalHour);

const DetailsForm = (props: IDetailsFormProps) => {
    const {
        alert,
        setAlert,
        setDataPoints,
    } = useContext(AlertContext);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [useCron, setUseCron] = useState(!!alert?.cronExpression)
    const [groups, setGroups] = useState([])
    const AlertTypeSelect = enumNamesToSelect(AlertType);

    const dispatch = useDispatch();

    useEffect(() => {
        let loadedDataPoints;
        (async () => {
            try {
                setIsLoading(true);
                loadedDataPoints = (await apiClient.get(dataPointsUrl, {params: {fields: fieldsDataPoints}})).data.results
                setDataPoints(loadedDataPoints)
            } catch (err) {
                setUiAlert({msg: "Couldn't load data points", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading(false);
            }
        })()
    }, []);


    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                let groupsOptions = (await apiClient.get(GroupsBaseUrl, {params: {fields: fieldsGroups}})).data.results
                setGroups(groupsOptions)
            } catch (err) {
                setUiAlert({msg: "Couldn't load groups", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading(false);
            }
        })()
    }, []);

    const onChange = (e: any) => {
        let {name, value} = e.target;
        setAlert({...alert, [name]: value});
    }

    const handleUseCron = (event) => {
        let useCronValue = false;
        if (event.target.value === 'true') {
            alert.intervalHour = 0
            useCronValue = true
        } else {
            alert.cronExpression = null
        }
        setUseCron(useCronValue)
    };

    const renderTypeParamsForm = () => {
        switch (alert.type) {
            case AlertType.DELTA: {
                return <DeltaParamsForm/>
            }
            case AlertType.EXPRESSION: {
                return <ExpressionParamsForm/>
            }
            case AlertType.DISCREPANCY: {
                return <DiscrepancyParamsForm/>
            }
            case AlertType.THRESHOLD: {
                return <ThresholdParamsForm/>
            }
            default:
                throw Error("alert type is not defined correctly");

        }
    }

    const handleTypeChange = (e) => {
        const newAlertType = e.target.value
        let alertParams;
        switch (newAlertType) {
            case AlertType.EXPRESSION: {
                alertParams = initialExpressionParams;
                break;
            }
            case AlertType.DELTA: {
                alertParams = initialDeltaParams;
                break;
            }
            case AlertType.DISCREPANCY: {
                alertParams = initialDiscrepancyParams;
                break;
            }
            case AlertType.THRESHOLD: {
                alertParams = initialThresholdParams;
                break;
            }

            default: {
                throw new Error("alert type is invalid")
            }
        }
        setAlert({...alert, type: newAlertType, params: alertParams})
    }

    return (
        <>
            <Typography fontFamily={"Quicksand"} fontWeight={600} variant="h5" style={{paddingBottom: "20px"}}>
                ALERT INFO
            </Typography>

            <Grid container direction={"row"} justifyContent={"flex-start"} alignItems={"center"}  gap={"10px"}>
                <Grid item xs={3.8} md={3.8}  >
                    <FormHelperText>
                        Name
                    </FormHelperText>
                    <TextField
                        autoFocus
                        fullWidth
                        value={alert?.name}
                        onChange={onChange}
                        size="small"
                        variant="outlined"
                        name="name"
                        className={"gridItem"}
                    />
                </Grid>

                <Grid item xs={3.8} md={3.8}>
                    <FormHelperText >
                        Alert Type
                    </FormHelperText>
                    <AlertTypeSelect
                        style={{height:"43px"}}
                        required
                        fullWidth
                        value={alert?.type}
                        onChange={handleTypeChange}
                        name="type"
                    />
                </Grid>

            </Grid>

            <Grid container direction={"row"} justifyContent={"flex-start"} alignItems={"flex-start"} gap={"10px"}>
                <Grid item xs={3.8} md={3.8} pt={"23px"} >
                    <TextField select name="group" variant={"outlined"} fullWidth
                               label={"Notification Group"}
                               disabled={groups.length === 0} onChange={onChange} defaultValue={alert.group}
                               style={{height:"43px"}}
                    >
                        {groups && !groups.length ?
                            <MenuItem value="" disabled={true} >
                                <em>No Available Groups</em>
                            </MenuItem> :
                            groups.map((group, index) => {
                                return <MenuItem key={index} value={group._id}>
                                    {group.name} - {group.method}
                                </MenuItem>
                            })
                        }
                    </TextField>

                </Grid>

                <Grid item xs={3.8} sm={3.8} md={3.8} >
                    <FormHelperText style={{paddingTop: "10px"}}>
                        Cron Or Interval Hour
                    </FormHelperText>
                    <CronOrIntervalSelect
                        style={{height:"43px"}}
                        required
                        fullWidth
                        value={useCron}
                        onChange={handleUseCron}
                        name="useCronExpression"
                    />
                </Grid>

                {!useCron && <Grid item xs={3.8} md={3.8}>
                    <FormHelperText style={{paddingTop: "10px"}}>
                        Interval Hour
                    </FormHelperText>
                    <TextField
                        fullWidth
                        value={alert?.intervalHour}
                        onChange={onChange}
                        size="small"
                        type="number"
                        InputProps={{ inputProps: { min: 0.5 } }}
                        variant="outlined"
                        name="intervalHour"
                        className={"gridItem"}

                    />
                </Grid>}

                {useCron && <Grid item xs={3.8} md={3.8}>
                    <FormHelperText style={{paddingTop: "10px"}}>
                        Cron Expression
                    </FormHelperText>
                    <TextField
                        fullWidth
                        value={alert?.cronExpression}
                        onChange={onChange}
                        size="small"
                        variant="outlined"
                        name="cronExpression"
                        className={"gridItem"}
                    />
                </Grid>}
            </Grid>

            <Grid container direction={"row"} justifyContent={"center"} alignItems={"center"}>
                <Typography variant="h5" style={{paddingTop:"50px"}}>
                    {alert?.type.toUpperCase()} - ALERT
                </Typography>
            </Grid>
            {renderTypeParamsForm()}

        </>
    )
}

export {DetailsForm}


